import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useIntl } from 'react-intl';

import { Button, ValidationMessage } from '@xsolla/ui-core';

import { useCurrentProject, useProjects } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { PencilIcon } from '../../ui';
import {
  CustomizeProjectNameForm,
  CustomizeProjectNameInput,
  CustomizingInputWrapper,
  IsShowModeButtonWrapper,
  NavigationInputWrapper,
  ProjectName,
  ValidationMessageWrapper,
  ValueLength
} from './style';
import { CustomProjectNameValidator } from './validator';

const maxNameLength = 20;

const CustomProjectName = (): JSX.Element => {
  const intl = useIntl();
  const { changeProject, getProjectById } = useProjects();
  const { currentProject, updateCurrentProject } = useCurrentProject();
  const { showErrorNotify } = useNotifications();

  const [isCustomizeMode, setCustomizeMode] = useState<boolean>();
  const [fetching, setFetching] = useState<boolean>(false);

  const onSubmit = (values: { name: string }) => {
    setFetching(true);
    if (currentProject) {
      updateCurrentProject({ ...currentProject, name: values.name.trim() })
        .then((res) => {
          const dashboardProject = getProjectById(res.id);

          if (dashboardProject) {
            changeProject({ ...dashboardProject, name: values.name.trim() });
          }
        })
        .catch((err) => {
          showErrorNotify(err);
        })
        .finally(() => {
          setCustomizeMode(false);
          setFetching(false);
        });
    }
  };

  return isCustomizeMode ? (
    <Form
      initialValues={{
        name: currentProject?.name
      }}
      validate={CustomProjectNameValidator}
      onSubmit={onSubmit}>
      {({ handleSubmit, pristine, errors, values: { name } }) => {
        return (
          <CustomizeProjectNameForm onSubmit={handleSubmit}>
            <CustomizingInputWrapper>
              <Field name="name">
                {({ input }) => (
                  <NavigationInputWrapper>
                    <CustomizeProjectNameInput
                      data-test-id="input-change-project-name"
                      uiSize="sm"
                      value={input.value}
                      onChange={(e) => {
                        input.onChange(e.target.value.slice(0, maxNameLength));
                      }}
                    />
                    <ValidationMessageWrapper>
                      <ValidationMessage>
                        {errors?.name && intl.formatMessage({ id: errors?.name })}
                      </ValidationMessage>
                    </ValidationMessageWrapper>
                  </NavigationInputWrapper>
                )}
              </Field>
              <ValueLength>
                {name?.length || 0}/{maxNameLength}
              </ValueLength>
            </CustomizingInputWrapper>
            <Button
              data-test-id="btn-change-project-name-save"
              style={{
                fontWeight: 700,
                marginLeft: '8px'
              }}
              onClick={handleSubmit}
              fetching={fetching}
              type="submit"
              look="link"
              palette="brand"
              size="md"
              disabled={pristine || errors?.name}>
              {intl.messages['navigation.save.project-name']}
            </Button>
          </CustomizeProjectNameForm>
        );
      }}
    </Form>
  ) : (
    <>
      <ProjectName data-test-id="project-name">{currentProject?.name}</ProjectName>
      <IsShowModeButtonWrapper
        data-test-id="btn-change-project-name"
        onClick={() => setCustomizeMode(true)}>
        <PencilIcon color="#0073F7" />
      </IsShowModeButtonWrapper>
    </>
  );
};

export default CustomProjectName;
